/**
 * HereditaryRiskSection — iter625 — Embedded version of the DKG checklist
 * mounted INSIDE the BHI Density section, BEFORE Gail/BCRAT assessment.
 *
 * Differences from standalone /dashboard/hereditary-risk page:
 *  • Renders without page chrome (no back button, no title duplication)
 *  • Shows "Нова оценка" button to reset and re-submit
 *  • Collapses result by default after assessment (saves space inside BHI)
 *  • Emits `hereditary-risk-changed` so BHI risk stratification updates live
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, FileCheck, RotateCcw, ChevronDown, ChevronUp, ShieldAlert, UserPlus } from "lucide-react";
import { toast } from "sonner";

const TIER_COLOR_MAP = {
  emerald: "bg-emerald-500 text-white",
  amber:   "bg-amber-500 text-white",
  orange:  "bg-orange-500 text-white",
  rose:    "bg-rose-500 text-white",
};

export default function HereditaryRiskSection({ onResultChange }) {
  const { API } = useAuth();
  const { isDark } = useTheme();

  const [catalog, setCatalog] = useState([]);
  const [answers, setAnswers] = useState({});
  const [maternalCount, setMaternalCount] = useState(0);
  const [paternalCount, setPaternalCount] = useState(0);
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("female");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [editMode, setEditMode] = useState(false);   // show form even if result exists
  const [recsExpanded, setRecsExpanded] = useState(false);

  const loadCurrent = async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/hereditary-checklist`);
      if (data.has_data) {
        setResult(data);
        if (onResultChange) onResultChange(data);
        const a = data.answers || {};
        setAnswers(a);
        setMaternalCount(a.maternal_line_additional_count || 0);
        setPaternalCount(a.paternal_line_additional_count || 0);
        if (a.age) setAge(String(a.age));
        if (a.sex) setSex(a.sex);
        setCatalog([]);  // catalog only returned when has_data is false
      } else {
        setCatalog(data.criteria_catalog || []);
      }
    } catch {
      toast.error("Грешка при зареждане.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCurrent(); /* eslint-disable-next-line */ }, [API]);

  // Need catalog even in edit mode → fetch separately if not present
  useEffect(() => {
    if (editMode && catalog.length === 0) {
      // Use answers keys as fallback when catalog is empty — same labels stored backend-side.
      // Easiest: refetch catalog by clearing the user's record temporarily isn't viable.
      // Instead embed a static label map matching backend DKG_CRITERIA.
      setCatalog(STATIC_CATALOG);
    }
  }, [editMode, catalog.length]);

  const toggle = (key) => setAnswers((s) => ({ ...s, [key]: !s[key] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...Object.fromEntries(
          Object.entries(answers).filter(([k]) => !["age", "sex", "maternal_line_additional_count", "paternal_line_additional_count", "note_bg"].includes(k))
        ),
        maternal_line_additional_count: parseInt(maternalCount) || 0,
        paternal_line_additional_count: parseInt(paternalCount) || 0,
        age: age ? parseInt(age) : null,
        sex,
      };
      const { data } = await axios.post(`${API}/bhi/hereditary-checklist`, payload);
      setResult(data);
      setEditMode(false);
      setRecsExpanded(true);
      if (onResultChange) onResultChange(data);
      window.dispatchEvent(new Event("hereditary-risk-changed"));
      toast.success(`${data.tier_label_bg} · ${data.score} точки`);
    } catch {
      toast.error("Грешка при изчислението.");
    } finally {
      setSubmitting(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/40 border-slate-700" : "bg-white border-slate-200";

  if (loading) {
    return <div className="py-6 flex items-center justify-center" data-testid="hereditary-section-loading">
      <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
    </div>;
  }

  // Compact result view (default after assessment)
  if (result && !editMode) {
    return (
      <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="hereditary-section-result">
        <div className="flex items-baseline gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-bold uppercase tracking-wider">DKG Наследствен риск</span>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${TIER_COLOR_MAP[result.tier_color]}`} data-testid="hereditary-section-tier">
            {result.tier_label_bg}
          </span>
          <span className="text-2xl font-bold tabular-nums ml-auto" data-testid="hereditary-section-score">
            {result.score} <span className="text-xs opacity-50">точки</span>
          </span>
        </div>

        {result.referral_indicated && (
          <div className={`p-2.5 rounded-lg mb-3 text-xs ${isDark ? "bg-orange-900/30 border border-orange-700/40 text-orange-100" : "bg-orange-50 border border-orange-200 text-orange-900"}`} data-testid="hereditary-section-referral">
            ⚠ <strong>Препоръка за генетична консултация</strong> — резултатът ≥100 точки. Насочване към медицински генетик за BRCA1/2 + multi-gene panel.
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setEditMode(true)} variant="outline" size="sm" data-testid="hereditary-reassess-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Нова оценка
          </Button>
          <Button onClick={() => setRecsExpanded(!recsExpanded)} variant="outline" size="sm" data-testid="hereditary-toggle-recs-btn">
            {recsExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
            {recsExpanded ? "Скрий" : "Покажи"} NCCN + AGO препоръки ({(result.recommendations || []).length})
          </Button>
          {/* iter1038 — BHI ↔ Family History bridge */}
          <Button asChild variant="outline" size="sm" data-testid="hereditary-add-relative-btn">
            <Link to="/dashboard/womens-health?tab=family">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Добави роднина
            </Link>
          </Button>
        </div>

        {recsExpanded && (
          <div className="space-y-2 mt-3" data-testid="hereditary-section-recs">
            {(result.recommendations || []).map((r, i) => (
              <div key={i} className={`p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={`hereditary-section-rec-${i}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <strong className="text-xs uppercase tracking-wider">{r.category_bg}</strong>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    r.priority === "very_high" ? "bg-rose-200 text-rose-900" :
                    r.priority === "high" ? "bg-orange-200 text-orange-900" :
                    r.priority === "enhanced" ? "bg-amber-200 text-amber-900" :
                    "bg-emerald-200 text-emerald-900"
                  }`}>{r.priority}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{r.action_bg}</p>
                <p className="text-[10px] mt-1 italic opacity-60">{r.evidence_bg}</p>
              </div>
            ))}
            <p className="text-[11px] mt-2 italic opacity-70">⚠ {result.disclaimer_bg}</p>
          </div>
        )}
      </div>
    );
  }

  // Edit/initial form
  return (
    <div className={`p-5 rounded-2xl border ${cardCls}`} data-testid="hereditary-section-form">
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert className="w-5 h-5 text-purple-500" />
        <h3 className="font-bold text-sm">Семейна анамнеза за рак на гърдата и яйчниците</h3>
      </div>
      <p className="text-[11px] opacity-70 mb-3">
        1:1 дигитализиран Checklist erbliche Belastung Brust/Gyn (Deutsche Krebsgesellschaft, 2025-09).
        Маркирай само наличните случаи в семейната ти анамнеза. Точките са според DKG скалата.
      </p>

      {/* iter1016 — връзката „Добави роднина" е премахната от формата за нова оценка
          (фамилната анамнеза се въвежда веднъж в „Моите данни"). */}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] opacity-70">Възраст</span>
          <input type="number" min="14" max="110" value={age} onChange={(e) => setAge(e.target.value)} data-testid="hereditary-age"
            className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] opacity-70">Пол</span>
          <select value={sex} onChange={(e) => setSex(e.target.value)} data-testid="hereditary-sex"
            className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}>
            <option value="female">жена</option>
            <option value="male">мъж</option>
            <option value="other">друго</option>
          </select>
        </label>
      </div>

      <div className="space-y-1.5 mb-4">
        {catalog.filter(c => !["maternal_line_additional_count", "paternal_line_additional_count"].includes(c.key)).map((c) => (
          <label key={c.key} data-testid={`hereditary-criterion-${c.key}`}
                 className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                   answers[c.key] ? (isDark ? "bg-purple-900/30 border border-purple-500/40" : "bg-purple-50 border border-purple-300") : "border border-transparent"
                 }`}>
            <input type="checkbox" checked={!!answers[c.key]} onChange={() => toggle(c.key)}
                   className="mt-0.5 accent-purple-500" />
            <span className="flex-1 text-sm">{c.label_bg}</span>
            <span className={`text-xs font-bold tabular-nums shrink-0 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
              +{c.score} т.
            </span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] opacity-70">Доп. случаи в майчината линия (×8 т.)</span>
          <input type="number" min="0" max="20" value={maternalCount} onChange={(e) => setMaternalCount(e.target.value)} data-testid="hereditary-maternal"
            className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] opacity-70">Доп. случаи в бащината линия (×6 т.)</span>
          <input type="number" min="0" max="20" value={paternalCount} onChange={(e) => setPaternalCount(e.target.value)} data-testid="hereditary-paternal"
            className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
        </label>
      </div>

      <div className="flex gap-2">
        <Button onClick={submit} disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
                data-testid="hereditary-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileCheck className="w-4 h-4 mr-2" />}
          Изчисли наследствен риск
        </Button>
        {result && (
          <Button onClick={() => setEditMode(false)} variant="outline" data-testid="hereditary-cancel-btn">
            Отказ
          </Button>
        )}
      </div>
    </div>
  );
}

// Static catalog mirroring backend DKG_CRITERIA — used when result already exists
// but user clicks "Нова оценка" (the GET endpoint doesn't return the catalog then).
const STATIC_CATALOG = [
  { key: "patient_triple_negative_maca_lt70",    score: 48, label_bg: "Диагностициран ли Ви е тройно-негативен рак на гърдата преди 70-годишна възраст?" },
  { key: "patient_ovarial_lt80",                 score: 35, label_bg: "Диагностициран ли Ви е рак на яйчниците преди 80-годишна възраст?" },
  { key: "patient_bilateral_maca_first",         score: 36, label_bg: "Диагностициран ли Ви е двустранен рак на гърдата?" },
  { key: "patient_unilateral_maca",              score: 15, label_bg: "Диагностициран ли Ви е рак на едната гърда?" },
  { key: "sister_daughter_niece_bilateral_maca", score: 38, label_bg: "Има ли Ваша сестра, дъщеря или племенница с двустранен рак на гърдата?" },
  { key: "sister_daughter_niece_unilateral_maca",score: 12, label_bg: "Има ли Ваша сестра, дъщеря или племенница с рак на едната гърда?" },
  { key: "sister_daughter_niece_ovarial",        score: 22, label_bg: "Има ли Ваша сестра, дъщеря или племенница с рак на яйчниците?" },
  { key: "male_relative_breast_cancer",          score: 30, label_bg: "Има мъж в моето семейство (баща, брат, син, чичо, племенник), който е диагностициран с рак на гърдата" },
  { key: "general_relative_maca_unilateral",     score: 3,  label_bg: "Едностранен рак на гърдата при по-далечна роднина (баба, леля, братовчедка)" },
  { key: "general_relative_maca_bilateral",      score: 5,  label_bg: "Двустранен рак на гърдата при по-далечна роднина (баба, леля, братовчедка)" },
  { key: "general_relative_ovarial",             score: 8,  label_bg: "Рак на яйчниците при по-далечна роднина (баба, леля, братовчедка)" },
  { key: "male_patient_breast_cancer",           score: 45, label_bg: "Аз съм мъж и съм диагностициран с едно- или двустранен рак на гърдата" },
];
